import { atom } from "jotai";

const openAtom = atom(false);
const openCountAtom = atom(0);

/**
 * Controls visibility of the "go to cart" popup shown after add-to-cart.
 *
 * Lives in its own module so the add-to-cart buttons (product card, bundle,
 * product details) can flip the atom WITHOUT statically importing the
 * `GoToCartPopUp` component — which is `dynamic()`-mounted once in
 * `Container`. Importing the atom from the component file would pull the whole
 * popup (Modal, Lottie, slider) into every product card and defeat the lazy load.
 *
 * Reads and writes as the plain boolean it has always been; every open also
 * bumps {@link goToCartOpenCountAtom} — see that atom for why.
 */
export const goToCartAtom = atom(
  (get) => get(openAtom),
  (get, set, open: boolean) => {
    // Only a closed -> open transition counts as a new opening. Adding another
    // product while the popup is already up must not bump the counter, or the
    // cross-sell sheet would remount and re-animate under the shopper mid-tap.
    if (open && !get(openAtom)) set(openCountAtom, get(openCountAtom) + 1);
    set(openAtom, open);
  },
);

/**
 * How many times the add-to-cart popup has been opened this session.
 *
 * A boolean can't express "it happened again": two adds in a row leave it `true`
 * throughout, so a consumer that needs to restart per add (the cross-sell sheet
 * re-reads the basket each time) has no edge to key off. This counter is that
 * edge — use it as a React `key` rather than trying to diff the boolean.
 */
export const goToCartOpenCountAtom = atom((get) => get(openCountAtom));
